import React, { useRef, useState } from 'react';
import { X, Save } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useProductStore } from '../../stores/productStore';
import { useTemplateStore } from '../../stores/templateStore';
import { useFolderStore } from '../../stores/folderStore';
import { LoadingOverlay } from '../common/LoadingOverlay';
import { ImageGroupPreview } from './ImageGroupPreview';
import { TemplateSelector } from './TemplateSelector';
import { TemplateValueEditor } from './TemplateValueEditor';
import { GroupSizeSelector } from './GroupSizeSelector';
import { PhotoOrderToggle } from './PhotoOrderToggle';
import { UnusedImages } from './UnusedImages';
import {
  BulkImportCommonSettings,
  type BulkImportCommonSettingsHandle
} from './BulkImportCommonSettings';
import type { ProcessingProgress } from '../../utils/imageProcessing';
import { 
  getInitialGroupSizes,
  createImageGroups,
  updateGroupSize,
  getDefaultPhotoImportOrder,
  applyPhotoImportOrder,
  type ImageGroup,
  type PhotoImportOrder,
} from '../../utils/imageImportUtils';
import type { ProductTemplate } from '../../utils/templates/productTemplates';

interface BulkImageEditModalProps {
  images: {
    files: File[];
    originals: string[];
    thumbnails: string[];
  };
  onClose: () => void;
  onSaved?: () => void;
}

export interface GroupSettings {
      categoryYahoo?: string;
      startPrice?: number;
      condition?: string;
      memo?: string;
      shippingMethods?: string[];
      customShippingMethods?: any[];
      shippingResponsibility?: "buyer" | "seller" | "";
      shippingLater?: boolean;
      shippingCOD?: boolean;
      nekoEazySize?: string;
      nekoEazyWeight?: string;
      yuPackSize?: string;
      yuPackWeight?: string;
      descriptionTemplateId?: string;
      productTemplateId?: string;
      autoExtension?: boolean;
      autoExtensionRate?: number;
      autoExtensionCount?: number;
    }

export function BulkImageEditModal({ images, onClose, onSaved }: BulkImageEditModalProps) {
  const commonSettingsRef = useRef<BulkImportCommonSettingsHandle>(null);
  const [showSizeSelector, setShowSizeSelector] = useState(true);
  const [imageGroups, setImageGroups] = useState<ImageGroup[]>([]);
  const [groupSizes, setGroupSizes] = useState<{ [key: string]: number }>({});
  const [defaultGroupSize, setDefaultGroupSize] = useState(4);
  const [unusedImages, setUnusedImages] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<ProcessingProgress | null>(null);
  const [currentProductIndex, setCurrentProductIndex] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState<ProductTemplate | null>(null);
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [deletedImages, setDeletedImages] = useState<Set<string>>(new Set());
  const [isCanceling, setIsCanceling] = useState(false);
  const [groupSettings, setGroupSettings] = useState<{
    [key: number]: GroupSettings;
  }>({});
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitMessage, setLimitMessage] = useState('');
  const [photoOrder, setPhotoOrder] = useState<PhotoImportOrder>(getDefaultPhotoImportOrder);

  const addProduct = useProductStore(state => state.addProduct);
  const updateProduct = useProductStore(state => state.updateProduct);
  const { templates } = useTemplateStore();
  const setCurrentFolder = useFolderStore(state => state.setCurrentFolder);

  const getOrderedImages = () => applyPhotoImportOrder(images, photoOrder);

  const getActiveImages = (order: PhotoImportOrder = photoOrder) => {
    const ordered = applyPhotoImportOrder(images, order);
    return {
      files: ordered.files.filter((_, index) => !deletedImages.has(ordered.thumbnails[index])),
      originals: ordered.originals.filter((_, index) => !deletedImages.has(ordered.thumbnails[index])),
      thumbnails: ordered.thumbnails.filter(thumb => !deletedImages.has(thumb)),
    };
  };

  const rebuildImageGroups = (order: PhotoImportOrder, sizes: { [key: string]: number } = groupSizes) => {
    const active = getActiveImages(order);
    return createImageGroups(active.files, active.originals, active.thumbnails, sizes);
  };

  const handlePhotoOrderChange = (order: PhotoImportOrder) => {
    setPhotoOrder(order);
    setImageGroups(rebuildImageGroups(order));
  };

  const handleSizeSelect = (size: number) => {
    const ordered = getOrderedImages();
    const initialSizes = getInitialGroupSizes(ordered.files.length, size);
    setDefaultGroupSize(size);
    setGroupSizes(initialSizes);
    
    const groups = createImageGroups(
      ordered.files,
      ordered.originals,
      ordered.thumbnails,
      initialSizes
    );
    
    setImageGroups(groups);
    setShowSizeSelector(false);
  };

  const handleGroupSizeChange = (groupIndex: number, newSize: number) => {
    const active = getActiveImages();

    const { updatedSizes, updatedGroups } = updateGroupSize(
      active.files,
      active.originals,
      active.thumbnails,
      groupSizes,
      groupIndex,
      newSize
    );

    setGroupSizes(updatedSizes);
    setImageGroups(updatedGroups);
  };

  const handleDeleteImage = (groupIndex: number, imageIndex: number) => {
    // Immutable update pattern
    setImageGroups(prevGroups => {
      const updatedGroups = [...prevGroups];
      const group = {...updatedGroups[groupIndex]};
      
      if (group) {
        const deletedThumbnail = group.thumbnails[imageIndex];
        setDeletedImages(prev => new Set([...prev, deletedThumbnail]));

        // Create new arrays without the deleted image
        const newFiles = [...group.files];
        newFiles.splice(imageIndex, 1);
        
        const newOriginals = [...group.originals];
        newOriginals.splice(imageIndex, 1);
        
        const newThumbnails = [...group.thumbnails];
        newThumbnails.splice(imageIndex, 1);

        // Create updated group with new arrays
        const updatedGroup = {
          ...group,
          files: newFiles,
          originals: newOriginals,
          thumbnails: newThumbnails
        };
        
        updatedGroups[groupIndex] = updatedGroup;
      }
      
      return updatedGroups;
    });

    // Update group sizes
    setGroupSizes(prevSizes => {
      const newGroupSizes = { ...prevSizes };
      const currentSize = newGroupSizes[groupIndex] || defaultGroupSize;
      newGroupSizes[groupIndex] = Math.max(1, currentSize - 1);
      return newGroupSizes;
    });
  };

  const handleReorderImages = (groupIndex: number, fromIndex: number, toIndex: number) => {
    setImageGroups(prev => {
      const newGroups = [...prev];
      const group = { ...newGroups[groupIndex] };
      
      // Create new arrays for immutable updates
      const newFiles = [...group.files];
      const newOriginals = [...group.originals];
      const newThumbnails = [...group.thumbnails];
      
      // Move files
      const [movedFile] = newFiles.splice(fromIndex, 1);
      newFiles.splice(toIndex, 0, movedFile);
      
      // Move originals
      const [movedOriginal] = newOriginals.splice(fromIndex, 1);
      newOriginals.splice(toIndex, 0, movedOriginal);
      
      // Move thumbnails
      const [movedThumbnail] = newThumbnails.splice(fromIndex, 1);
      newThumbnails.splice(toIndex, 0, movedThumbnail);
      
      // Create updated group with new arrays
      const updatedGroup = {
        ...group,
        files: newFiles,
        originals: newOriginals,
        thumbnails: newThumbnails
      };
      
      newGroups[groupIndex] = updatedGroup;
      return newGroups;
    });
  };

  const handleSettingsChange = (groupIndex: number, updates: GroupSettings) => {
    setGroupSettings(prev => ({
      ...prev,
      [groupIndex]: {
        ...prev[groupIndex],
        ...updates
      }
    }));
  };

  const handleTemplateEdit = (template: ProductTemplate) => {
    setSelectedTemplate(template);
    setShowTemplateEditor(true);
  };

  const handleTemplateSave = (updatedTemplate: ProductTemplate) => {
    setSelectedTemplate(updatedTemplate);
  };

  const handleCancel = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsCanceling(true);
    await new Promise(resolve => setTimeout(resolve, 300));
    onClose();
  };

  const handleCreateProducts = async () => {
    if (imageGroups.length === 0 || isProcessing) return;

    const validationError = commonSettingsRef.current?.validate() ?? null;
    if (validationError) {
      setError(validationError);
      return;
    }

    await useFolderStore.getState().loadFolders();

    const folderId = await commonSettingsRef.current?.resolveFolderId();
    if (!folderId) {
      setError('フォルダの取得に失敗しました');
      return;
    }

    setCurrentFolder(folderId);
    const itemNumberSettings = commonSettingsRef.current?.getItemNumberSettings() ?? null;

    setIsProcessing(true);
    setError(null);
    setCurrentProductIndex(0);

    const MAX_CONCURRENT = 4;

    let stopped = false;
    let completed = 0;
    let errorOccurred = false;
    let limitHit = false;

    const processGroup = async (i: number) => {
      if (stopped) return;
      setCurrentProductIndex(i);
      const group = imageGroups[i];
      const settings = groupSettings[i] || {};

      if (group.files.length === 0) {
        completed++;
        return;
      }

      setProgress({
        current: completed + 1,
        total: imageGroups.length,
        phase: 'processing'
      });

      try {
        // Use modal dropdown only — not templateStore.activeTemplateId
        const newProduct = await addProduct(false);

        let baseProduct = selectedTemplate?.fields
          ? {
              ...newProduct,
              ...selectedTemplate.fields,
              customShippingMethods: [...selectedTemplate.fields.customShippingMethods]
            }
          : newProduct;

        if (settings.productTemplateId) {
          const groupTemplate = templates.find(t => t.id === settings.productTemplateId);
          if (groupTemplate) {
            baseProduct = {
              ...baseProduct,
              ...groupTemplate.fields,
              customShippingMethods: [...groupTemplate.fields.customShippingMethods]
            };
          }
        }

        const updatedProduct = {
          ...baseProduct,
          folderId,
          images: [...group.originals],
          ...(settings.categoryYahoo && { categoryYahoo: settings.categoryYahoo }),
          ...(settings.startPrice && { startPrice: settings.startPrice }),
          ...(settings.condition && { condition: settings.condition }),
          ...(settings.memo && { memo: settings.memo }),
          ...(settings.descriptionTemplateId && { descriptionTemplateId: settings.descriptionTemplateId }),
          ...(settings.autoExtension !== undefined && { autoExtension: settings.autoExtension }),
          ...(settings.autoExtensionRate !== undefined && { autoExtensionRate: settings.autoExtensionRate }),
          ...(settings.autoExtensionCount !== undefined && { autoExtensionCount: settings.autoExtensionCount }),
          ...(settings.shippingMethods && {
            nekoposu: settings.shippingMethods.includes('nekoposu'),
            nekoCompact: settings.shippingMethods.includes('nekoCompact'),
            nekoEazy: settings.shippingMethods.includes('nekoEazy'),
            yuPacketMini: settings.shippingMethods.includes('yuPacketMini'),
            yuPacket: settings.shippingMethods.includes('yuPacket'),
            yuPacketPlus: settings.shippingMethods.includes('yuPacketPlus'),
            yuPack: settings.shippingMethods.includes('yuPack')
          }),
          ...(settings.customShippingMethods && settings.customShippingMethods?.length > 0 && {
            customShippingMethods: settings.customShippingMethods
          }),
          ...(settings.shippingResponsibility && { shippingResponsibility: settings.shippingResponsibility }),
          ...(settings.shippingLater && { shippingLater: settings.shippingLater }),
          ...(settings.shippingCOD && { shippingCOD: settings.shippingCOD }),
          ...(settings.nekoEazySize && { nekoEazySize: settings.nekoEazySize }),
          ...(settings.nekoEazyWeight && { nekoEazyWeight: settings.nekoEazyWeight }),
          ...(settings.yuPackSize && { yuPackSize: settings.yuPackSize }),
          ...(settings.yuPackWeight && { yuPackWeight: settings.yuPackWeight })
        };

        if (itemNumberSettings) {
          Object.assign(updatedProduct, {
            itemNumber: itemNumberSettings.itemNumber,
            itemNumber2: itemNumberSettings.itemNumber2,
            itemNumberPrefix: itemNumberSettings.itemNumberPrefix,
            itemNumberSuffix: itemNumberSettings.itemNumberSuffix,
            itemNumberSerial: itemNumberSettings.itemNumberSerial,
            memoPrefix: itemNumberSettings.memoPrefix,
            memoSuffix: itemNumberSettings.memoSuffix,
            memoSerial: itemNumberSettings.memoSerial
          });
        }

        await updateProduct(updatedProduct);
        completed++;
      } catch (productError) {
        // Product limit error handling
        if (
          productError &&
          ((typeof productError === 'object' &&
            'message' in productError &&
            typeof (productError as any).message === 'string' &&
            (productError as any).message.includes('製品登録数の上限')) ||
            (typeof productError === 'object' &&
              'message' in productError &&
              typeof (productError as any).message === 'string' &&
              (productError as any).message.includes('これ以上商品を登録できません')))
        ) {
          const errorMessage = (productError as any).message as string;
          const isMaxPlan = errorMessage.includes('上限の1000個に達しています');

          if (isMaxPlan) {
            setLimitMessage(
              `${completed}個の商品が正常に作成されました。\n\n${i + 1}個目の商品作成時に上限に達しました。\n\nこれ以上商品を登録できません。上限の1000個に達しています。\n\n新しい商品を作成するには、既存の商品を削除してから再度お試しください。`
            );
          } else {
            setLimitMessage(
              `${completed}個の商品が正常に作成されました。\n\n${i + 1}個目の商品作成時に上限に達しました。\n\n${errorMessage}`
            );
          }
          setShowLimitModal(true);
          limitHit = true;
          setIsProcessing(false);
          setProgress(null);
          setCurrentProductIndex(0);
          stopped = true;
          errorOccurred = true;
        } else {
          throw productError;
        }
      }
    };

    try {
      // Multi-threaded (concurrent) processing
      let index = 0;
      const total = imageGroups.length;

      // Worker function
      const worker = async () => {
        while (index < total && !stopped) {
          const current = index++;
          await processGroup(current);
        }
      };

      // Start workers
      const workers = Array.from({ length: Math.min(MAX_CONCURRENT, total) }, () => worker());
      await Promise.all(workers);

      if (!limitHit && !errorOccurred) {
        onClose();
        if (onSaved) onSaved();
      }
    } catch (error) {
      console.error('Error creating products:', error);
      setError('商品の作成中にエラーが発生しました');
    } finally {
      setIsProcessing(false);
      setProgress(null);
      setCurrentProductIndex(0);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        {!showSizeSelector && (
        <div className="bg-white rounded-lg max-w-4xl w-full mx-2 sm:mx-4 max-h-[90vh] flex flex-col min-w-0">
          <div className="p-3 sm:p-4 border-b flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between shrink-0">
            <h2 className="text-lg sm:text-xl font-medium text-gray-900">写真のグループ分け</h2>
            <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
              <motion.button
                onClick={handleCancel}
                disabled={isCanceling}
                whileTap={{ scale: 0.95 }}
                className="relative flex-1 sm:flex-none px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-gray-100 rounded-md overflow-hidden"
              >
                <AnimatePresence mode="wait">
                  {isCanceling ? (
                    <motion.div
                      key="canceling"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="absolute inset-0 flex items-center justify-center bg-gray-200"
                    >
                      <span className="font-medium text-gray-700">キャンセル中...</span>
                    </motion.div>
                  ) : (
                    <motion.span
                      key="cancel"
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 20 }}
                    >
                      キャンセル
                    </motion.span>
                  )}
                </AnimatePresence>
              </motion.button>
              <motion.button
                onClick={handleCreateProducts}
                disabled={imageGroups.length === 0 || isProcessing}
                whileTap={{ scale: 0.95 }}
                className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 rounded-md disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed whitespace-nowrap"
              >
                商品を作成
              </motion.button>
            </div>
          </div>
        

        <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 sm:p-6 min-w-0">
            <div className="space-y-6">
              {error && (
                <div className="p-3 bg-red-50 text-red-600 text-sm rounded-md">
                  {error}
                </div>
              )}

              <BulkImportCommonSettings
                ref={commonSettingsRef}
                imageCount={images.files.length}
              />

              <div className="rounded-lg border border-purple-100 bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 p-4 sm:p-5 shadow-sm min-w-0">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                  <span className="text-sm sm:text-base font-medium text-gray-800 shrink-0">
                    一括テンプレートを選択:
                  </span>
                  <div className="flex-1 min-w-0 w-full">
                    <TemplateSelector
                      templates={templates}
                      selectedTemplate={selectedTemplate}
                      onSelect={setSelectedTemplate}
                      onEdit={handleTemplateEdit}
                      compact
                    />
                  </div>
                </div>
              </div>

              <PhotoOrderToggle
                photoOrder={photoOrder}
                onPhotoOrderChange={handlePhotoOrderChange}
              />

              <div className="space-y-8">
                {imageGroups.map((group, index) => (
                  <ImageGroupPreview
                    key={`${group.prefix}-${index}`}
                    group={group}
                    groupSize={groupSizes[index] || defaultGroupSize}
                    onGroupSizeChange={(size) => handleGroupSizeChange(index, size)}
                    index={index}
                    totalImages={images.files.length - deletedImages.size}
                    onDeleteImage={handleDeleteImage}
                    onReorderImages={handleReorderImages}
                    onSettingsChange={handleSettingsChange}
                    settings={groupSettings[index] || {}}
                    selectedTemplate={selectedTemplate ?? null}
                  />
                ))}
              </div>

              {unusedImages.length > 0 && (
                <UnusedImages
                  images={unusedImages}
                  onDelete={(index) => {
                    setUnusedImages(prev => prev.filter((_, i) => i !== index));
                  }}
                />
              )}
            </div>
        </div>
      </div>
      )}

      {showSizeSelector &&(
        <GroupSizeSelector
              onSelect={handleSizeSelect}
              onClose={onClose}
              defaultSize={defaultGroupSize}
              totalImages={images.files.length}
            />
      )}

      {showTemplateEditor && selectedTemplate && (
        <TemplateValueEditor
          template={selectedTemplate}
          onSave={handleTemplateSave}
          onClose={() => setShowTemplateEditor(false)}
        />
      )}

      {/* Product Limit Modal */}
      {showLimitModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0">
                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900">製品登録数の上限に達しました</h3>
            </div>
            
            <div className="mb-6">
              <p className="text-sm text-gray-600 whitespace-pre-line">
                {limitMessage}
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowLimitModal(false);
                  setLimitMessage('');
                  // Close the main modal after user confirms
                  setTimeout(() => {
                    onClose();
                    if (onSaved) onSaved();
                  }, 100);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                確認
              </button>
            </div>
          </div>
        </div>
      )}
      {isProcessing && (
        <LoadingOverlay 
          message="商品を作成してインポート中です。今しばらくお待ちください..."
          progress={progress ?? undefined}
          subMessage={
            imageGroups.length > 0
              ? `商品 ${currentProductIndex + 1}/${imageGroups.length} を処理中...`
              : undefined
          }
        />
      )}
    </div>
  );
}